<template>
  <div class="session-wrapper justify-center justify-md-start" aria-live="polite">
    <div class="session-left"></div>
    <div class="session-right text-center">
      <div class="session-content" role="main">
        <router-link :to="{ name: 'login' }" aria-label="Login page">
          <img :src="logotype"
               id="focus-target"
               class="img-responsive mb-3"
               tabindex="0"
               alt="Green Geeks logo"
               aria-label="Green Geeks logo"
               title="Green Geeks logo"
               role="banner"/>
        </router-link>

        <h1 class="mb-3 login-heading">Password Reset</h1>

        <v-form v-if="!isHashAvailable" class="mb-4">
          <div v-if="!show_explanation">
            <v-text-field
              label="E-mail"
              outlined
              v-model.trim="request_model.email"
              data-vv-name="email"
              data-vv-as="Email"
              v-validate="rules.email"
              :error-messages="errors.collect('email')">
            </v-text-field>
            <gg-btn type="submit" tile block dark large :loading="loading" @click.prevent="resetPasswordRequest">
              Reset Password
            </gg-btn>
            <p><br/><router-link :to="{ name: 'login' }" >Go Back</router-link></p>
          </div>
          <div v-else class="body-2 font-weight-regular">
            <p>
              If you have a GreenGeeks Account, a password reset link has been
              sent to your email. Please check this message for further instructions.
            </p>
            <p>
              Password reset links expire after two hours.
            </p>
          </div>
        </v-form>

        <v-form v-else>
          <div v-if="show_password_form">
            <p class="body-2 font-weight-regular">Enter in your new
              password.</p>
            <v-text-field
              label="Password"
              ref="password"
              :type="passwordInputType"
              outlined
              v-model="do_model.password"
              data-vv-name="password"
              data-vv-as="Password"
              v-validate="rules.password"
              :error-messages="errors.collect('password')"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  :ripple="false"
                  class="mt-0 mr-1"
                  @click="togglePasswordInput"
                >
                  <v-icon color="#C2C2C2">{{ passwordToggleIcon }}</v-icon>
                </v-btn>
              </template>
            </v-text-field>

            <v-text-field
              label="Confirm Password"
              type="password"
              outlined
              v-model="do_model.confirmPassword"
              data-vv-name="confirmPassword"
              data-vv-as="Confirm Password"
              v-validate="rules.confirmPassword"
              :error-messages="errors.collect('confirmPassword')">
            </v-text-field>
            <div>
              <gg-btn
                type="submit"
                block
                tile
                large
                :loading="loading"
                @click.prevent="doResetPassword">
                Reset Password
              </gg-btn>
            </div>
          </div>
          <div v-else>
            <v-progress-circular
              :size="50"
              color="primary"
              indeterminate
            />
          </div>
        </v-form>
      </div>
    </div>
    <cookie-law theme="dark-lime">
      <template slot-scope="props">
        <p>
          This website uses cookies to ensure you get the best experience on our services.
          <a href="https://www.greengeeks.com/legal/privacy" target="_blank">More details</a>
        </p>
        <gg-btn color="primary" tile @click="props.accept">Got it!</gg-btn>
      </template>
    </cookie-law>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CookieLaw from 'vue-cookie-law'

export default {
  name: 'ForgotPassword',
  components: {
    CookieLaw,
  },
  data: () => ({
    request_model: {
      email: process.env.VUE_APP_DEV_USERNAME,
    },
    do_model: {
      password: '',
      confirmPassword: ''
    },
    rules: {
      email: {
        required: true,
        email: true
      },
      password: {
        required: true
      },
      confirmPassword: {
        required: true,
        confirmed: 'password'
      }
    },
    loading: false,
    show_explanation: false,
    show_password_form: false,
    passwordInputType: 'password',
  }),
  computed: {
    ...mapGetters(['isAuthenticated', 'brand']),
    /**
     * Check if page param contains hash param
     * @returns {boolean}
     */
    isHashAvailable () {
      return this.$route.params.hash !== undefined
    },
    logotype () {
      return this.$vuetify.theme.dark ? this.brand.appLogo : this.brand.darkLogo
    },
    passwordToggleIcon () {
      return this.passwordInputType === 'password' ? 'mdi-eye' : 'mdi-eye-off'
    },
  },
  created () {
    if (this.isHashAvailable) {
      this.checkResetToken()
    }
  },
  methods: {
    ...mapActions([
      'requestResetPassword',
      'verifyResetToken',
      'doRequestPassword',
      'reportError',
      'reportSuccess',
    ]),
    togglePasswordInput () {
      this.passwordInputType = this.passwordInputType === 'password' ? 'text' : 'password'
    },
    async resetPasswordRequest () {
      try {
        this.loading = true
        const valid = await this.$validator.validateAll()
        if (valid) {
          const response = await this.requestResetPassword(this.request_model.email)
          this.show_explanation = true
          this.reportSuccess(response.resultmsg)
        }
        this.loading = false
      } catch (e) {
        this.loading = false
        this.reportError(e)
      }
    },
    async checkResetToken () {
      try {
        const token = this.$route.params.hash

        const response = await this.verifyResetToken(token)
        if (response.result === 0) {
          this.reportError(response.resultmsg)
          this.$router.push({ name: 'request_reset_password' })
        } else {
          this.reportSuccess(response.resultmsg)
          this.show_password_form = true
        }
      } catch (e) {
        this.reportError(e)
      }
    },
    async doResetPassword () {
      try {
        const isValid = await this.$validator.validateAll()

        if (isValid) {
          this.loading = true
          const data = {
            email_address: this.request_model.email,
            token: this.$route.params.hash,
            new_password: this.do_model.password,
            confirm_password: this.do_model.confirmPassword
          }

          const response = await this.doRequestPassword(data)

          if (response.status === 0) {
            this.reportError(response.resultmsg)
          } else {
            this.reportSuccess(response.resultmsg)
          }
          this.loading = false
          this.$router.push({ name: 'login' })
        }
      } catch (e) {
        this.loading = false
        this.reportError(e)
      }
    }
  },
}
</script>

<style scoped lang="scss">
  .cookie-button {
    border-radius: 2px;
    background-color: #4caf50 !important;
    &:hover {
      background-color: #97D058 !important;
    }
  }
</style>
